/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { NavigationBarItemData, NavigationBarActionData, NavigationBarLinkItemData, ShowLinkAsActive } from "@octopusdeploy/design-system-components";
import { NavigationBar, Tooltip } from "@octopusdeploy/design-system-components";
import type { UserResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { AnalyticLinkLocationProvider } from "@octopusdeploy/portal-analytics";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { projectsRoute } from "@octopusdeploy/portal-routes";
import { isNotNull } from "@octopusdeploy/type-utils";
import { Environment } from "@octopusdeploy/utilities";
import * as React from "react";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import configurationSelectors from "~/areas/configuration/reducers/selectors";
import { WelcomeDialog } from "~/areas/dashboard/Onboarding/WelcomeDialog/WelcomeDialog";
import { client, repository, session } from "~/clientInstance";
import drawerActions from "~/components/ContextualHelpLayout/reducers/actions";
import DeprecationNotificationsPoller from "~/components/Navbar/NotificationPoller/DeprecationNotificationsPoller";
import { ProjectSwitcherNavigationBarItem } from "~/components/Navbar/ProjectSwitcherNavigationBarItem";
import { SpaceSwitcherNavigationBarItem } from "~/components/Navbar/SpaceSwitcherNavigationBarItem";
import { UserAccountMenu } from "~/components/Navbar/UserAccountMenu";
import { helpInteractionType, useDispatchHelpInteraction } from "~/components/Navbar/analytics/useDispatchHelpInteraction";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import SearchAndOpenPopover from "~/globalSearch/SearchAndOpenPopover";
import routeLinks from "../../routeLinks";
import { isDrawerOpen } from "../ContextualHelpLayout/reducers";
import type { SpaceContext } from "../StandardLayout/SpaceLoader";
import { isSpaceNotFound, isSpecificSpaceContext } from "../StandardLayout/SpaceLoader";
import DevBanner from "./DevBanner";
import GitVariablesNotifications from "./NotificationPoller/GitVariablesNotifications";
import LicenceNotificationPoller from "./NotificationPoller/LicenceNotificationPoller";
import UpdateAvailableNotificationPoller from "./NotificationPoller/UpdateAvailableNotificationPoller";
import NotificationsMenu from "./NotificationsMenu";
import styles from "./style.module.less";
interface NavbarProps {
    spaceContext: SpaceContext;
}
export const Navbar = ({ spaceContext }: NavbarProps) => {
    const [showDevBanner, setShowDevBanner] = React.useState(Environment.isInDevelopmentMode());
    const isLargerThanIpad = useMediaQuery({ query: `(min-width: 811px)` });
    const navigationItems: NavigationBarItemData[] = [...getNavigationItems(spaceContext)];
    const actions: NavigationBarActionData[] = [...getNavigationActions(session.currentUser!, spaceContext, isLargerThanIpad)];
    return (<div id="topnavbar" className={styles.topnavbar}>
            {showDevBanner ? <DevBanner onClose={() => setShowDevBanner(false)}/> : undefined}

            <div className={styles.navbarUplift}>
                <NavigationBar logo={<SpaceSwitcherNavigationBarItem spaceContext={spaceContext}/>} items={navigationItems} actions={actions}/>
                <UpdateAvailableNotificationPoller />
                <LicenceNotificationPoller />
                <GitVariablesNotifications />
                <DeprecationNotificationsPoller />
                <WelcomeDialog />
            </div>
        </div>);
};
function getNavigationItems(spaceContext: SpaceContext) {
    const createLinkNavigationItem = (linkData: LinkData): NavigationBarLinkItemData => {
        return {
            type: "link",
            href: linkData.href,
            label: linkData.label,
            showLinkAsActive: linkData.showAsActive ?? "if path partially matches",
        };
    };
    const navigationItems: NavigationBarItemData[] = [];
    if (isSpecificSpaceContext(spaceContext)) {
        navigationItems.push(createLinkNavigationItem({ label: "Dashboard", href: routeLinks.root, showAsActive: "if path matches exactly" }));
        const hasProjectPermissions = session.currentPermissions!.scopeToSpace(client.spaceId).hasPermissionInAnyScope(Permission.ProjectView);
        const hasInfrastructurePermissions = isAllowed({ permission: [Permission.MachineView, Permission.EnvironmentView, Permission.WorkerView], wildcard: true });
        const hasTenantPermissions = session.currentPermissions!.scopeToSpace(client.spaceId).hasPermissionInAnyScope(Permission.TenantView);
        const hasInsightsPermissions = session.currentPermissions!.scopeToSpace(client.spaceId).hasPermissionInAnyScope(Permission.InsightsReportView);
        const hasLibraryPermissions = isAllowed({ permission: [Permission.FeedView, Permission.LifecycleEdit, Permission.VariableView, Permission.ActionTemplateView, Permission.CertificateView], wildcard: true });
        if (hasProjectPermissions) {
            const { href } = createLinkNavigationItem({ label: "Projects", href: projectsRoute.generateUrl({ spaceId: spaceContext.Id }) });
            navigationItems.push({
                type: "custom",
                key: "Projects",
                content: <ProjectSwitcherNavigationBarItem projectsHref={href}/>,
                fallbackLink: {
                    href: href,
                    label: "Projects",
                    showLinkAsActive: "if path partially matches",
                },
            });
        }
        if (hasInfrastructurePermissions) {
            navigationItems.push(createLinkNavigationItem({ href: routeLinks.infrastructure.root, label: "Infrastructure" }));
        }
        if (hasTenantPermissions) {
            navigationItems.push(createLinkNavigationItem({ href: routeLinks.tenants, label: "Tenants" }));
        }
        if (hasInsightsPermissions) {
            navigationItems.push(createLinkNavigationItem({ href: routeLinks.insights.reports, label: "Insights" }));
        }
        if (hasLibraryPermissions) {
            navigationItems.push(createLinkNavigationItem({ href: routeLinks.library.root, label: "Library" }));
        }
    }
    if (!isSpaceNotFound(spaceContext) || spaceContext.isAlsoInSystemContext) {
        const rootRouteLinks = isSpaceNotFound(spaceContext) ? routeLinks.withoutSpace() : routeLinks;
        const hasTaskPermissions = session.currentPermissions!.hasPermissionInAnyScope(Permission.TaskView);
        if (hasTaskPermissions) {
            navigationItems.push(createLinkNavigationItem({ href: rootRouteLinks.tasks.rootForSpace(client.spaceId), label: "Tasks" }));
        }
    }
    return navigationItems;
}
function getNavigationActions(currentUser: UserResource, spaceContext: SpaceContext, isLargerThanIpad: boolean): NavigationBarActionData[] {
    return [
        { key: "search", content: <SearchAndOpenPopover /> },
        { key: "configuration", content: <ConfigurationNavigationActionButton spaceContext={spaceContext}/> },
        { key: "notifications", content: <NotificationsMenu /> },
        isLargerThanIpad ? { key: "help-menu", content: <HelpNavigationActionButton /> } : null,
        {
            key: "user-menu",
            content: (<AnalyticLinkLocationProvider location={"Profile Menu"}>
                    <UserAccountMenu currentUser={currentUser} spaceContext={spaceContext}/>
                </AnalyticLinkLocationProvider>),
        },
    ].filter(isNotNull);
}
function HelpNavigationActionButton() {
    const dispatch = useDispatch();
    const dispatchHelpInteraction = useDispatchHelpInteraction();
    const drawerOpen = useSelector(isDrawerOpen);
    const toggleDrawer = useCallback(() => {
        dispatchHelpInteraction(drawerOpen ? helpInteractionType.close : helpInteractionType.open, HelpNavigationActionButton.name);
        dispatch(drawerActions.toggleDrawer());
    }, [dispatch, dispatchHelpInteraction, drawerOpen]);
    const isHelpSidebarEnabled = useSelector(configurationSelectors.createFeatureEnabledSelector((x) => x.isHelpSidebarEnabled));
    if (!isHelpSidebarEnabled) {
        return null;
    }
    return (<Tooltip content="Help">
            <NavigationBar.IconButton onClick={toggleDrawer} accessibleName={drawerOpen ? "Hide help" : "Show help"} aria-expanded={drawerOpen} icon={<svg width="24" height="24" viewBox="0 0 24 24">
                        <path d="M12 2.39999C6.67502 2.39999 2.40002 6.71249 2.40002 12C2.40002 17.325 6.67502 21.6 12 21.6C17.2875 21.6 21.6 17.325 21.6 12C21.6 6.71249 17.2875 2.39999 12 2.39999ZM12 20.4C7.35002 20.4 3.60002 16.65 3.60002 12C3.60002 7.38749 7.35002 3.59999 12 3.59999C16.6125 3.59999 20.4 7.38749 20.4 12C20.4 16.65 16.6125 20.4 12 20.4ZM11.4 15.3C10.875 15.3 10.5 15.7125 10.5 16.2C10.5 16.725 10.875 17.1 11.4 17.1C11.8875 17.1 12.3 16.725 12.3 16.2C12.3 15.7125 11.8875 15.3 11.4 15.3ZM13.0875 7.19999H10.875C9.48752 7.19999 8.40002 8.32499 8.40002 9.71249V10.05C8.40002 10.3875 8.66252 10.65 9.00002 10.65C9.30002 10.65 9.60002 10.3875 9.60002 10.05V9.71249C9.60002 8.99999 10.1625 8.39999 10.875 8.39999H13.0875C13.8 8.39999 14.4 8.99999 14.4 9.71249C14.4 10.1625 14.1375 10.6125 13.725 10.8375L11.475 11.9625C11.0625 12.1875 10.8 12.6375 10.8 13.125V13.8C10.8 14.1375 11.0625 14.4 11.4 14.4C11.7 14.4 12 14.1375 12 13.8V13.125C12 13.0875 12 13.05 12.0375 13.0125L14.2875 11.8875C15.075 11.4375 15.6 10.6125 15.6 9.71249C15.6 8.32499 14.475 7.19999 13.0875 7.19999Z"/>
                    </svg>}/>
        </Tooltip>);
}
function ConfigurationNavigationActionButton({ spaceContext }: {
    spaceContext: SpaceContext;
}) {
    const rootRouteLinks = isSpaceNotFound(spaceContext) ? routeLinks.withoutSpace() : routeLinks;
    if (!hasConfigurationPermissions(spaceContext)) {
        return null;
    }
    return (<Tooltip content="Configuration">
            <NavigationBar.IconLink href={rootRouteLinks.configuration.root} showLinkAsActive="if path partially matches" accessibleName="Configuration" icon={<svg width="24" height="24" viewBox="0 0 24 24">
                        <path d="M8.70011 11.9999C8.70011 10.1999 10.1626 8.6999 12.0001 8.6999C13.8001 8.6999 15.3001 10.1999 15.3001 11.9999C15.3001 13.8374 13.8001 15.2999 12.0001 15.2999C10.1626 15.2999 8.70011 13.8374 8.70011 11.9999ZM12.0001 9.8999C10.8376 9.8999 9.90011 10.8749 9.90011 11.9999C9.90011 13.1624 10.8376 14.0999 12.0001 14.0999C13.1251 14.0999 14.1001 13.1624 14.1001 11.9999C14.1001 10.8749 13.1251 9.8999 12.0001 9.8999ZM4.83761 11.0624L3.33761 9.6749C2.92511 9.2999 2.81261 8.6999 3.11261 8.2124L4.23761 6.2249C4.50011 5.7374 5.10011 5.5124 5.62511 5.6999L7.57511 6.2999C8.10011 5.9249 8.66261 5.5874 9.22511 5.3624L9.67511 3.3749C9.78761 2.8124 10.2751 2.3999 10.8376 2.3999H13.1251C13.6876 2.3999 14.1751 2.8124 14.2876 3.3749L14.7376 5.3624C15.3001 5.5874 15.8626 5.9249 16.3876 6.2999L18.3376 5.6999C18.8626 5.5124 19.4626 5.7374 19.7251 6.2249L20.8501 8.2124C21.1501 8.6999 21.0376 9.2999 20.6251 9.6749L19.1251 11.0624C19.1626 11.3624 19.2001 11.6999 19.2001 11.9999C19.2001 12.3374 19.1626 12.6749 19.1251 12.9749L20.6251 14.3624C21.0376 14.7374 21.1501 15.3374 20.8501 15.8249L19.7251 17.8124C19.4626 18.2999 18.8626 18.5249 18.3376 18.3374L16.3876 17.7374C15.8626 18.1124 15.3001 18.4124 14.7376 18.6749L14.2876 20.6624C14.1751 21.2249 13.6876 21.5999 13.1251 21.5999H10.8376C10.2751 21.5999 9.78761 21.2249 9.67511 20.6624L9.22511 18.6749C8.66261 18.4124 8.10011 18.1124 7.57511 17.7374L5.62511 18.3374C5.10011 18.5249 4.50011 18.2999 4.23761 17.8124L3.11261 15.8249C2.81261 15.3374 2.92511 14.7374 3.33761 14.3624L4.83761 12.9749C4.80011 12.6749 4.80011 12.3374 4.80011 11.9999C4.80011 11.6999 4.80011 11.3624 4.83761 11.0624ZM8.32511 7.2749L7.83761 7.6499L5.28761 6.8249L4.12511 8.8124L6.11261 10.6124L6.03761 11.2124C6.00011 11.4749 6.00011 11.7374 6.00011 11.9999C6.00011 12.2999 6.00011 12.5624 6.03761 12.8249L6.11261 13.4249L4.12511 15.2249L5.28761 17.2124L7.83761 16.3874L8.32511 16.7624C8.73761 17.0999 9.18761 17.3624 9.71261 17.5499L10.2751 17.8124L10.8376 20.3999H13.1251L13.6876 17.8124L14.2501 17.5499C14.7751 17.3624 15.2251 17.0999 15.6376 16.7624L16.1251 16.3874L18.6751 17.2124L19.8376 15.2249L17.8501 13.4249L17.9251 12.8249C17.9626 12.5624 18.0001 12.2999 18.0001 11.9999C18.0001 11.7374 17.9626 11.4749 17.9251 11.2124L17.8501 10.6124L19.8376 8.8124L18.6751 6.8249L16.1251 7.6499L15.6376 7.2749C15.2251 6.9374 14.7751 6.6749 14.2501 6.4499L13.6876 6.2249L13.1251 3.5999H10.8376L10.2751 6.2249L9.71261 6.4499C9.18761 6.6749 8.73761 6.9374 8.32511 7.2749Z"/>
                    </svg>}/>
        </Tooltip>);
}
function hasConfigurationPermissions(spaceContext: SpaceContext) {
    if (!isSpaceNotFound(spaceContext) || spaceContext.isAlsoInSystemContext) {
        return (session.currentPermissions!.scopeToSpace(repository.spaceId).hasAnyPermissions() ||
            isAllowed({
                permission: [Permission.AdministerSystem, Permission.EventView, Permission.TeamView, Permission.UserView, Permission.UserRoleView, Permission.SpaceView, Permission.SpaceEdit, Permission.SpaceCreate, Permission.SpaceDelete],
                wildcard: true,
            }));
    }
    return false;
}
interface LinkData {
    label: string;
    href: LinkHref;
    showAsActive?: ShowLinkAsActive;
}
